import React, { useEffect, useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { EditableText } from '../ui/EditableText';
import { BrandSectionLayout } from './BrandSectionLayout';
import { supabase } from '@/lib/supabase';
import { hexToRgbString, hexToHslString } from '@/lib/colorUtils';

interface BrandColorsSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

interface ColorInfo {
  name: string;
  hex: string;
}

interface ColorDisplay {
  name: string;
  hex: string;
  rgb: string;
  hsl: string;
}

const ColorBlock: React.FC<{ color: ColorDisplay; textColor?: string }> = ({ color, textColor = '#000000' }) => {
  return (
    <div
      className="h-full flex flex-col justify-start p-6"
      style={{ backgroundColor: color.hex, color: textColor }}
    >
      <h3 className="text-2xl font-bold mb-3">{color.name}</h3>
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex gap-2">
          <span className="font-semibold">HEX</span>
          <span className="font-mono">{color.hex}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold">RGB</span>
          <span className="font-mono">{color.rgb}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold">HSL</span>
          <span className="font-mono">{color.hsl}</span>
        </div>
      </div>
    </div>
  );
};

export const BrandColorsSection: React.FC<BrandColorsSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const { currentWorkspace } = useWorkspace();
  const [primaryColor, setPrimaryColor] = useState<ColorDisplay | null>(null);
  const [secondaryColor, setSecondaryColor] = useState<ColorDisplay | null>(null);
  const [accentColor, setAccentColor] = useState<ColorDisplay | null>(null);
  const [lightColor, setLightColor] = useState<ColorDisplay | null>(null);
  const [darkColor, setDarkColor] = useState<ColorDisplay | null>(null);

  useEffect(() => {
    const loadColorPreset = async () => {
      if (!state.currentBrandId) {
        setDefaultColors();
        return;
      }

      try {
        const { data: brand, error } = await supabase
          .from('brands')
          .select('color_preset_id')
          .eq('id', state.currentBrandId)
          .maybeSingle();

        if (error) throw error;

        if (brand?.color_preset_id) {
          const { data: template, error: templateError } = await supabase
            .from('color_preset_templates')
            .select('*')
            .eq('id', brand.color_preset_id)
            .maybeSingle();

          if (templateError) throw templateError;

          if (template && template.colors && template.colors.length > 0) {
            const colors = template.colors as ColorInfo[];
            processColors(colors);
            return;
          }
        }

        setDefaultColors();
      } catch (error) {
        console.error('Error loading color preset:', error);
        setDefaultColors();
      }
    };

    loadColorPreset();
  }, [state.currentBrandId, state.brandData, currentWorkspace]);

  const processColors = (colors: ColorInfo[]) => {
    const displayColors: ColorDisplay[] = colors.map(color => ({
      name: color.name,
      hex: color.hex.toUpperCase(),
      rgb: hexToRgbString(color.hex),
      hsl: hexToHslString(color.hex),
    }));

    if (displayColors.length >= 3) {
      setPrimaryColor(displayColors[0]);
      // Secondary and Accent are optional - check if they have valid hex values
      const secondary = displayColors[1];
      const accent = displayColors[2];
      const light = displayColors[3];
      const dark = displayColors[4];

      setSecondaryColor(secondary && secondary.hex && secondary.hex !== '' ? secondary : null);
      setAccentColor(accent && accent.hex && accent.hex !== '' ? accent : null);
      setLightColor(light || { name: 'Light', hex: '#FFFFFF', rgb: hexToRgbString('#FFFFFF'), hsl: hexToHslString('#FFFFFF') });
      setDarkColor(dark || { name: 'Dark', hex: '#000000', rgb: hexToRgbString('#000000'), hsl: hexToHslString('#000000') });
    }
  };

  const setDefaultColors = () => {
    const colors: ColorInfo[] = [
      { name: 'Primary', hex: state.brandData?.colors?.primary || '#FF6B6B' },
      { name: 'Secondary', hex: state.brandData?.colors?.secondary || '' },
      { name: 'Accent', hex: state.brandData?.colors?.accent || '' },
      { name: 'Light', hex: '#FFFFFF' },
      { name: 'Dark', hex: '#000000' },
    ];
    processColors(colors);
  };

  if (!primaryColor || !lightColor || !darkColor) {
    return null;
  }

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      <div className="h-full flex gap-16">
        {/* Left Column - 30% width with title and description */}
        <div className="flex flex-col" style={{ width: '30%' }}>
          <div className="flex flex-col items-start gap-4">
            <EditableText
              value={section.content.colorsTitle || 'Brand Colors'}
              onChange={(value) => updateSectionContent('colorsTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold mb-8"
              placeholder="Brand Colors"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'colorsTitle'}
              onEditStart={() => setEditingElement('colorsTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('colorsTitle', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                fontWeight: 'bold',
                ...section.content.colorsTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />

            <EditableText
              value={section.content.colorsDescription || 'Our carefully curated color palette reflects our brand identity and creates a cohesive visual experience.'}
              onChange={(value) => updateSectionContent('colorsDescription', value)}
              element="p"
              className="text-lg leading-relaxed"
              placeholder="Our carefully curated color palette reflects our brand identity"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'colorsDescription'}
              onEditStart={() => setEditingElement('colorsDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('colorsDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.colorsDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>
        </div>

        {/* Right Column - 70% width with color blocks */}
        <div style={{ width: '70%' }}>
          <div className="h-full flex">
            {/* Left side of right column - Primary color taking full height */}
            <div className="w-1/2 h-full">
              <ColorBlock color={primaryColor} textColor="#FFFFFF" />
            </div>

            {/* Right side of right column - Dynamic grid layout based on available colors */}
            <div className="w-1/2 h-full flex flex-col">
              {/* Layout 1: No secondary AND no accent - Light and Dark in separate rows */}
              {!secondaryColor && !accentColor && (
                <>
                  <div className="h-1/2">
                    <ColorBlock color={lightColor} textColor="#000000" />
                  </div>
                  <div className="h-1/2">
                    <ColorBlock color={darkColor} textColor="#FFFFFF" />
                  </div>
                </>
              )}

              {/* Layout 2: Both Secondary AND Accent present - Secondary, Accent, then Light and Dark side by side */}
              {secondaryColor && accentColor && (
                <>
                  <div className="h-1/3">
                    <ColorBlock color={secondaryColor} textColor="#FFFFFF" />
                  </div>
                  <div className="h-1/3">
                    <ColorBlock color={accentColor} textColor="#FFFFFF" />
                  </div>
                  <div className="h-1/3 flex">
                    <div className="w-1/2">
                      <ColorBlock color={lightColor} textColor="#000000" />
                    </div>
                    <div className="w-1/2">
                      <ColorBlock color={darkColor} textColor="#FFFFFF" />
                    </div>
                  </div>
                </>
              )}

              {/* Layout 3: Secondary present but NO accent - Secondary full row, then Light and Dark side by side */}
              {secondaryColor && !accentColor && (
                <>
                  <div className="h-1/2">
                    <ColorBlock color={secondaryColor} textColor="#FFFFFF" />
                  </div>
                  <div className="h-1/2 flex">
                    <div className="w-1/2">
                      <ColorBlock color={lightColor} textColor="#000000" />
                    </div>
                    <div className="w-1/2">
                      <ColorBlock color={darkColor} textColor="#FFFFFF" />
                    </div>
                  </div>
                </>
              )}

              {/* Layout 4: Accent present but NO secondary - Accent full row, then Light and Dark side by side */}
              {!secondaryColor && accentColor && (
                <>
                  <div className="h-1/2">
                    <ColorBlock color={accentColor} textColor="#FFFFFF" />
                  </div>
                  <div className="h-1/2 flex">
                    <div className="w-1/2">
                      <ColorBlock color={lightColor} textColor="#000000" />
                    </div>
                    <div className="w-1/2">
                      <ColorBlock color={darkColor} textColor="#FFFFFF" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BrandSectionLayout>
  );
};
